import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import moment from 'moment';
import gaiaTheme from '../../../../root-components/theme';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { IApiKey } from '../../../../shared/services/api-key/api-key.model';
import ApiKeyService from '../../../../shared/services/api-key/api-key.service';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { SettingsMobileNavbar } from '../../../../root-components/navigation/settings-mobile-navbar';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { CreateApiKeyDialog } from '../../../dialogs/settings/create-api-key-dialog';

export const ApiKeysPage = () => {
  const navigate = useNavigate();
  const apiKeyService = new ApiKeyService();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [apiKeys, setApiKeys] = useState<IApiKey[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    getApiKeys();
  }, []);

  const getApiKeys = () => {
    apiKeyService.getAll()
      .then(response => {
        setIsLoading(false);
        const keys = Array.isArray(response) ? response : (response as any).rows ?? [];
        setApiKeys(keys);
        setTotalRecords(keys.length);
      })
      .catch(err => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const headCells: IHeadCell[] = [
    { id: 'details', numeric: false, label: 'Description', sortable: false, isHeading: true },
    { id: 'createdAt', numeric: false, label: 'Created', sortable: false },
    { id: 'lastUsed', numeric: false, label: 'Last Used', sortable: false },
  ];

  const rows = apiKeys.map(key => ({
    id: key.id,
    details: key.details || '—',
    createdAt: moment(key.createdAt).format('DD/MM/YYYY HH:mm'),
    lastUsed: key.lastUsed ? moment(key.lastUsed).format('DD/MM/YYYY HH:mm') : 'Never',
  }));

  const handleSelected = (selectedRowIds: string[]) => {
    if (selectedRowIds[0]) {
      navigate(`/settings/company-settings/api-keys/${selectedRowIds[0]}`);
    }
  };

  const handleCreateSuccess = (createdKey: IApiKey) => {
    setOpenCreateDialog(false);
    setNewlyCreatedKey(createdKey.key);
    getApiKeys();
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    userNotificationsContext.openSnackbar({ open: true, message: 'API key copied to clipboard' });
  };

  return (
    <PageOuterContainer>
      {isMobileMode && (
        <Box sx={{ pb: 4 }}>
          <SettingsMobileNavbar />
        </Box>
      )}
      <PageHeading
        title='API Keys'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonText='Create New Key'
        actionButtonHandler={() => setOpenCreateDialog(true)}
      />

      {newlyCreatedKey && (
        <Alert
          severity='success'
          sx={{ mb: 3 }}
          onClose={() => setNewlyCreatedKey(null)}
        >
          <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
            API key created. Copy it now, it will not be shown again in full.
          </Typography>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography
              variant='body2'
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                bgcolor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {newlyCreatedKey}
            </Typography>
            <Tooltip title='Copy'>
              <IconButton size='small' onClick={() => handleCopyKey(newlyCreatedKey)}>
                <ContentCopy fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Alert>
      )}

      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          haveCheckboxSelect={false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>

      {openCreateDialog && (
        <CreateApiKeyDialog
          open={openCreateDialog}
          closeHandler={() => setOpenCreateDialog(false)}
          successHandler={handleCreateSuccess}
        />
      )}
    </PageOuterContainer>
  );
};
